import { useAuth } from "@clerk/clerk-react";
import {
  Backdrop,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { FormEvent, useState } from "react";
import { Route, Routes, useNavigate } from "react-router-dom";
import { AdminBentoCategory } from "schemas/admin";
import { BentoBrand } from "schemas/dashboard";
import { CustomEvent } from "schemas/functions";

import Alert from "components/Alert";
import StyledPage from "components/Styled/Page";
import NewBrand from "features/Admin/Brand/NewBrand";
import { fetcherAuth } from "utils/api";
import { useAlert } from "utils/useAlert";

const MergeBrands = () => {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const [message, severity, setAlert, closeAlert] = useAlert();

  const [query, setQuery] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<BentoBrand[]>([]);
  const [categoriesById, setCategoriesById] = useState<{
    [key: number]: AdminBentoCategory;
  }>({});

  const [idsToMerge, setIdsToMerge] = useState<Set<number>>(new Set());
  const [idToKeep, setIdToKeep] = useState<number | null>(null);

  const fetchCategories = async () => {
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/admin/bento-categories`,
        "GET",
      );
      const byId: { [key: number]: AdminBentoCategory } = {};
      for (const category of res.bentoCategories) {
        byId[category.id] = category;
      }
      setCategoriesById(byId);
    } catch (error) {
      setAlert(error.message || "An error occurred", "warning");
    }
  };

  const fetchBrands = async () => {
    setLoading(true);
    if (Object.keys(categoriesById).length === 0) {
      fetchCategories();
    }
    try {
      const encodedQuery = encodeURIComponent(query);
      const res = await fetcherAuth(
        getToken,
        `/api/admin/bento-brands?query=${encodedQuery}&page=1&per_page=50`,
        "GET",
      );
      setResults(
        [...res.bentoBrands].sort((a, b) => {
          const aInactive = !!(a.mergedIntoId || a.isArchived);
          const bInactive = !!(b.mergedIntoId || b.isArchived);
          return Number(aInactive) - Number(bInactive);
        }),
      );
      setIdsToMerge(new Set());
      setIdToKeep(null);
    } catch (error) {
      setAlert(error.message || "An error occurred", "warning");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    fetchBrands();
  };

  const handleSelectToggle = (id: number) => {
    setIdsToMerge((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
    setIdToKeep((prev) => (prev === id ? null : prev));
  };

  const handleIdToKeepToggle = (id: number) => {
    setIdToKeep((prev) => (prev === id ? null : id));
    setIdsToMerge((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  const handleSelectAll = () => {
    setIdsToMerge(
      new Set(results.filter((r) => !r.mergedIntoId).map((r) => r.id)),
    );
  };

  const handleClearAll = () => {
    setIdsToMerge(new Set());
    setIdToKeep(null);
  };

  const handleMerge = async () => {
    if (!idToKeep) {
      setAlert("Select a brand to merge into.", "warning");
      return;
    }
    const filteredIdsToMerge = [...idsToMerge].filter((id) => id !== idToKeep);
    if (filteredIdsToMerge.length === 0) {
      setAlert(
        "Select at least one brand to merge (other than the merge target).",
        "warning",
      );
      return;
    }
    setLoading(true);
    try {
      await fetcherAuth(
        getToken,
        "/api/admin/bento-brands/merge",
        "POST",
        {},
        {
          id_to_keep: idToKeep,
          ids_to_merge: filteredIdsToMerge,
        },
      );
      setAlert(
        `Merged ${filteredIdsToMerge.length} brand(s) into brand #${idToKeep}.`,
        "success",
      );
      setIdsToMerge(new Set());
      setIdToKeep(null);
      fetchBrands();
    } catch (error) {
      setAlert(error.message || "Merge failed", "warning");
    } finally {
      setLoading(false);
    }
  };

  return (
    <StyledPage
      content={
        <>
          <Backdrop
            sx={{ color: "#fff", zIndex: 9999, position: "absolute" }}
            open={loading}
          >
            <CircularProgress />
          </Backdrop>

          <Typography variant="h3" sx={{ mt: 2, mb: 2 }}>
            <strong>Merge Brands</strong>
          </Typography>

          <form onSubmit={handleSubmit}>
            <Grid container sx={{ mb: 2 }} spacing={1}>
              <Grid item xs>
                <TextField
                  label="Search brands"
                  fullWidth
                  placeholder="Name, username, or website"
                  value={query}
                  onChange={(e: CustomEvent) => setQuery(e.target.value)}
                />
              </Grid>
              <Grid item xs="auto">
                <Button type="submit" variant="contained" color="primary">
                  Search
                </Button>
              </Grid>
            </Grid>
          </form>

          {results.length > 0 && (
            <>
              <Grid container spacing={1} sx={{ mb: 1 }} alignItems="center">
                <Grid item xs />
                <Grid item xs="auto">
                  <Button
                    variant="contained"
                    color="error"
                    onClick={handleMerge}
                    disabled={idsToMerge.size === 0 || !idToKeep}
                  >
                    Merge ({idsToMerge.size} selected)
                    {idToKeep &&
                      (() => {
                        const brand = results.find((r) => r.id === idToKeep);
                        return brand
                          ? ` into ${brand.brandName} (${brand.id})`
                          : null;
                      })()}
                  </Button>
                </Grid>
              </Grid>

              <TableContainer
                sx={{
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: grey[200],
                  maxHeight: "calc(100vh - 380px)",
                }}
              >
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        {(() => {
                          const selectableIds = results
                            .filter((r) => !r.mergedIntoId)
                            .map((r) => r.id);
                          const allSelected =
                            selectableIds.length > 0 &&
                            selectableIds.every((id) => idsToMerge.has(id));
                          const someSelected = selectableIds.some((id) =>
                            idsToMerge.has(id),
                          );
                          return (
                            <Checkbox
                              size="small"
                              checked={allSelected}
                              indeterminate={someSelected && !allSelected}
                              onChange={() =>
                                allSelected
                                  ? handleClearAll()
                                  : handleSelectAll()
                              }
                            />
                          );
                        })()}
                      </TableCell>
                      <TableCell padding="checkbox">Into</TableCell>
                      <TableCell>Brand</TableCell>
                      <TableCell>Website</TableCell>
                      <TableCell>Instagram</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {results.map((brand) => (
                      <TableRow
                        key={brand.id}
                        hover
                        sx={{
                          cursor: "pointer",
                          backgroundColor:
                            idToKeep === brand.id
                              ? "#e8f5e9"
                              : brand.mergedIntoId || brand.isArchived
                                ? grey[100]
                                : undefined,
                        }}
                        onClick={() => navigate(`${brand.id}`)}
                      >
                        <TableCell
                          padding="checkbox"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Checkbox
                            size="small"
                            checked={idsToMerge.has(brand.id)}
                            onChange={() => handleSelectToggle(brand.id)}
                            disabled={!!brand.mergedIntoId}
                          />
                        </TableCell>
                        <TableCell
                          padding="checkbox"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Checkbox
                            size="small"
                            checked={idToKeep === brand.id}
                            onChange={() => handleIdToKeepToggle(brand.id)}
                            disabled={!!brand.mergedIntoId}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ lineHeight: 1.2 }}>
                            <Typography variant="body2" fontWeight={500}>
                              {brand.brandName}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {brand.id}
                              {brand.mergedIntoId
                                ? ` · merged into ${brand.mergedIntoId}`
                                : ""}
                              {brand.isArchived ? " · archived" : ""}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {brand.website}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {brand.instagramUsername}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          <Alert
            message={message}
            severity={severity}
            closeAlert={closeAlert}
          />

          <Routes>
            <Route
              path="/:id"
              element={
                <NewBrand
                  brands={results}
                  setBrands={setResults}
                  categoriesById={categoriesById}
                  handleClose={() =>
                    navigate("/admin/merge-brands", { replace: true })
                  }
                />
              }
            />
          </Routes>
        </>
      }
    />
  );
};

export default MergeBrands;
